import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Mail, 
  Phone, 
  Calendar, 
  CheckCircle2, 
  Building2, 
  ExternalLink,
  Trash2,
  Filter,
  Download,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InventoryLeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, contacted, qualified, converted

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/inventory-leads`);
      setLeads(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/inventory-leads/${id}`, {
        status: status
      });
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/cms/inventory-leads/${id}`);
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const exportToCSV = () => {
    const filteredLeads = getFilteredLeads();
    if (filteredLeads.length === 0) {
      alert('No leads to export');
      return;
    }

    const headers = ['Name', 'Email', 'Phone', 'Company', 'Source', 'Status', 'Submitted At'];
    const csvContent = [
      headers.join(','),
      ...filteredLeads.map(lead => [
        `"${lead.name}"`,
        `"${lead.email}"`,
        `"${lead.phone || ''}"`,
        `"${lead.company || ''}"`,
        `"${lead.source}"`,
        `"${lead.status}"`,
        `"${formatDate(lead.submitted_at)}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredLeads = () => {
    if (filter === 'all') return leads;
    return leads.filter(lead => lead.status === filter);
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-purple-100 text-purple-800',
      converted: 'bg-green-100 text-green-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'converted', label: 'Converted' }
  ];

  const filteredLeads = getFilteredLeads();

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="inventory-leads-manager">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light text-[#2d2d2d] flex items-center gap-3">
            <Users className="w-8 h-8 text-[#0A1A50]" />
            Inventory Leads
          </h1>
          <p className="text-[#666] mt-2">
            Total leads: {leads.length} | 
            New: {leads.filter(l => l.status === 'new').length} |
            Qualified: {leads.filter(l => l.status === 'qualified').length}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Dropdown */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#666]" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20"
              data-testid="leads-filter"
            >
              <option value="all">All Leads</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
            </select>
          </div>

          {/* Export Button */}
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="border-[#0A1A50] text-[#0A1A50]"
            data-testid="export-leads-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Leads List */}
      <div className="space-y-4">
        {filteredLeads.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#666]">
              {filter === 'all' ? 'No leads yet.' : `No ${filter} leads.`}
            </p>
          </div>
        ) : (
          filteredLeads.map((lead) => (
            <div
              key={lead.id}
              className={`bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow ${
                lead.status === 'new' ? 'border-l-4 border-[#0A1A50]' : ''
              }`}
              data-testid={`lead-card-${lead.id}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  {/* Header with name and status */}
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="text-xl font-normal text-[#2d2d2d]">{lead.name}</h3>
                    <span className={`px-3 py-1 text-xs rounded-full font-medium uppercase ${getStatusBadge(lead.status)}`}>
                      {lead.status}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {lead.source.replace('_', ' ')}
                    </span>
                  </div>
                  
                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                    <div className="flex items-center gap-2 text-[#2d2d2d]">
                      <Mail className="w-4 h-4 text-[#0A1A50]" />
                      <a href={`mailto:${lead.email}`} className="hover:text-[#0A1A50] hover:underline">
                        {lead.email}
                      </a>
                    </div>
                    
                    {lead.phone && (
                      <div className="flex items-center gap-2 text-[#2d2d2d]">
                        <Phone className="w-4 h-4 text-[#0A1A50]" />
                        <a href={`tel:${lead.phone}`} className="hover:text-[#0A1A50] hover:underline">
                          {lead.phone}
                        </a>
                      </div>
                    )}
                    
                    {lead.company && (
                      <div className="flex items-center gap-2 text-[#2d2d2d]">
                        <Building2 className="w-4 h-4 text-[#0A1A50]" />
                        {lead.company}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-[#666] text-sm">
                      <Calendar className="w-4 h-4" />
                      {formatDate(lead.submitted_at)}
                    </div>
                  </div>

                  {/* Redirect URL if present */}
                  {lead.redirect_url && (
                    <div className="bg-gray-50 p-3 rounded-lg flex items-center gap-2">
                      <ExternalLink className="w-4 h-4 text-[#0A1A50]" />
                      <span className="text-sm text-[#666]">Requested:</span>
                      <a 
                        href={lead.redirect_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#0A1A50] hover:underline truncate"
                      >
                        {lead.redirect_url}
                      </a>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="ml-4 flex flex-col gap-2">
                  {/* Status Change Dropdown */}
                  <select
                    value={lead.status}
                    onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1A50]/20"
                    data-testid={`lead-status-${lead.id}`}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Delete Button */}
                  <Button
                    onClick={() => deleteLead(lead.id)}
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`delete-lead-${lead.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InventoryLeadsManager;
